import { Flex, Tabs } from "@mantine/core";
import { useCallback, useState } from "react";

import { useSendDeskCollection } from "../../api/hooks/deskCollection/deskCollection/useSendDeskCollection.ts";
import { useUpdateDeskCollection } from "../../api/hooks/deskCollection/deskCollection/useUpdateDeskCollection.ts";
import { useMessageTemplates } from "../../api/hooks/deskCollection/messageTemplate/useMessageTemplates.ts";
import Layout from "../../components/Layout/Layout.tsx";
import Preloader from "../../components/Preloader/Preloader.tsx";
import Title from "../../components/Title/Title.tsx";
import { OrderBy, UpdateDeskCollectionInput } from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { getTemplateOptions } from "../../types/deskCollection/messageTemplate.ts";
import { tabs } from "./constants.ts";
import DeskCollectionAddInvoice from "./DeskCollectionAddInvoice.tsx";
import DeskCollectionAutogroup from "./DeskCollectionAutogroup.tsx";
import { TabValue } from "./types.ts";

const DeskCollectionToolsPage = () => {
  const [activeTab, setActiveTab] = useState<string>(TabValue.Autogroup);

  const [{ data: templatesResponse, fetching: fetchingTemplates }] =
    useMessageTemplates({
      orderBy: {
        createdAt: OrderBy.Desc,
      },
    });
  const templateOptions = getTemplateOptions(
    templatesResponse?.messageTemplates
  );

  const [, updateDeskCollection] = useMutationNotificationWrapper(
    useUpdateDeskCollection(),
    {
      success: {
        message: "Desk Collection record has been successfully updated",
      },
    }
  );

  const [, sendDeskCollection] = useMutationNotificationWrapper(
    useSendDeskCollection(),
    { success: { show: false }, error: { show: false } }
  );

  const handleUpdateDeskCollection = useCallback(
    (id: number, input: UpdateDeskCollectionInput) => {
      return updateDeskCollection({ id, input });
    },
    [updateDeskCollection]
  );

  const handleSendDeskCollectionMessage = useCallback(
    async (id: number, message: string) => {
      return sendDeskCollection({ id, input: { message } });
    },
    [sendDeskCollection]
  );

  return (
    <>
      <Layout>
        <Preloader loading={fetchingTemplates} />
        <Flex direction="column" gap={{ base: 24, sm: 20 }}>
          <Flex gap={20} justify="space-between">
            <Title size="h1">Desk Collection Tools</Title>
          </Flex>
          <Tabs
            value={activeTab}
            variant="outline"
            keepMounted={false}
            onTabChange={(tab) => setActiveTab(tab ?? TabValue.Autogroup)}
          >
            <Tabs.List>
              {tabs.map((item, index) => {
                return (
                  <Tabs.Tab key={index} value={item.value}>
                    <Flex align="center">{item.children ?? item.label}</Flex>
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
            <Tabs.Panel value={TabValue.Autogroup}>
              <DeskCollectionAutogroup
                templateOptions={templateOptions}
                onInlineEdit={handleUpdateDeskCollection}
                onSend={handleSendDeskCollectionMessage}
              />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.AddInvoice}>
              <DeskCollectionAddInvoice
                templateOptions={templateOptions}
                onInlineEdit={handleUpdateDeskCollection}
                onSend={handleSendDeskCollectionMessage}
              />
            </Tabs.Panel>
          </Tabs>
        </Flex>
      </Layout>
    </>
  );
};

export default DeskCollectionToolsPage;
